/**
 * Package Management Page
 * Allows administrators to manage packages, roles, and permissions
 * @component PackageManagement
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Checkbox,
  Menu,
  MenuItem as MuiMenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  MonetizationOn as MonetizationOnIcon,
  Timeline as TimelineIcon,
  Sync as SyncIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Build as BuildIcon,
  Star as StarIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import Layout from '../../../common/layout/Layout';
import { Package, getAllPackages, createPackage, updatePackage, syncBusinessStatuses } from '../../../services/packageService';
import { Permission, getAllPermissions, getRolePermissions, updateRolePermissions } from '../../../services/permissionService';
import PackageCard from '../../../components/PackageCard';
import '../BusinessSettings/BusinessSettings.css';

interface CreatePackageData {
  name: string;
  description: string;
  price: number;
  currency: string;
  max_businesses: number;
  target_audience: string;
  is_active: boolean;
  is_popular: boolean;
}

const PackageManagement: React.FC = () => {
  const { language } = useLanguage();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Permissions state
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<{ [role: string]: string[] }>({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedPackageForMenu, setSelectedPackageForMenu] = useState<Package | null>(null);

  // Form states
  const [createFormData, setCreateFormData] = useState<CreatePackageData>({
    name: '',
    description: '',
    price: 0,
    currency: 'USD',
    max_businesses: 1,
    target_audience: 'masters',
    is_active: false,
    is_popular: false
  });

  const [editFormData, setEditFormData] = useState<CreatePackageData>({
    name: '',
    description: '',
    price: 0,
    currency: 'USD',
    max_businesses: 1,
    target_audience: 'masters',
    is_active: false,
    is_popular: false
  });


  const [tabValue, setTabValue] = useState(0);

  // Toggle states for billing period and currency
  const [billingPeriod, setBillingPeriod] = useState<'month' | 'year'>('month');
  const [displayCurrency, setDisplayCurrency] = useState<'USD' | 'UAH'>('USD');

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  // Load permissions when role permissions tab is selected
  useEffect(() => {
    if (tabValue === 1) {
      fetchPermissions();
    }
  }, [tabValue]);

  const fetchPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const allPermissions = await getAllPermissions();
      setPermissions(allPermissions);

      // Load permissions for each role
      const roles = ['client', 'master', 'owner', 'admin', 'super_admin'];
      const rolePerms: { [role: string]: string[] } = {};

      for (const role of roles) {
        try {
          const perms = await getRolePermissions(role);
          rolePerms[role] = perms.map(p => p.name);
        } catch (error) {
          console.error(`Failed to load permissions for role ${role}:`, error);
          rolePerms[role] = [];
        }
      }

      setRolePermissions(rolePerms);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка завантаження дозволів' : 'Failed to load permissions',
        severity: 'error'
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handlePermissionChange = (role: string, permissionName: string, checked: boolean) => {
    setRolePermissions(prev => ({
      ...prev,
      [role]: checked
        ? [...(prev[role] || []), permissionName]
        : (prev[role] || []).filter(p => p !== permissionName)
    }));
  };

  const handleSavePermissions = async () => {
    try {
      setPermissionsLoading(true);

      // Save permissions for each role
      for (const [role, permissionNames] of Object.entries(rolePermissions)) {
        await updateRolePermissions(role, permissionNames);
      }

      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Дозволи успішно збережено' : 'Permissions saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to save permissions:', error);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка збереження дозволів' : 'Failed to save permissions',
        severity: 'error'
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleResetPermissions = async () => {
    if (confirm(language === 'ua' ? 'Скинути дозволи до початкових?' : 'Reset permissions to default?')) {
      await fetchPermissions();
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Дозволи скинуто до початкових' : 'Permissions reset to default',
        severity: 'info'
      });
    }
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllPackages();
      setPackages(data);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
      setError('Не вдалося завантажити пакети');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePackage = async () => {
    console.log('🔄 handleCreatePackage called');
    console.log('📝 Current form data:', createFormData);

    // Form validation
    if (!createFormData.name.trim()) {
      console.log('❌ Validation failed: Package name is empty');
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Назва пакета обов\'язкова' : 'Package name is required',
        severity: 'error'
      });
      return;
    }

    if (createFormData.price < 0) {
      console.log('❌ Validation failed: Price is negative', createFormData.price);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Ціна не може бути від\'ємною' : 'Price cannot be negative',
        severity: 'error'
      });
      return;
    }

    if (createFormData.max_businesses < 0) {
      console.log('❌ Validation failed: Max businesses is negative', createFormData.max_businesses);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Кількість бізнесів не може бути від\'ємною' : 'Max businesses cannot be negative',
        severity: 'error'
      });
      return;
    }

    console.log('✅ Form validation passed');

    try {
      console.log('📡 Calling createPackage API with data:', createFormData);

      const result = await createPackage(createFormData);
      console.log('✅ API call successful, result:', result);

      setCreateDialogOpen(false);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Пакет створено успішно' : 'Package created successfully',
        severity: 'success'
      });

      console.log('🔄 Fetching updated packages list');
      await fetchPackages();

      // Reset form
      console.log('🔄 Resetting form data');
      setCreateFormData({
        name: '',
        description: '',
        price: 0,
        currency: 'USD',
        max_businesses: 1,
        target_audience: 'masters',
        is_active: false,
        is_popular: false
      });

      console.log('🎉 Package creation completed successfully');

    } catch (err) {
      console.error('❌ Failed to create package:', err);

      // More detailed error logging
      if (err instanceof Error) {
        console.error('❌ Error message:', err.message);
        console.error('❌ Error stack:', err.stack);
      }

      setSnackbar({
        open: true,
        message: language === 'ua'
          ? `Помилка створення пакета: ${err instanceof Error ? err.message : 'Невідома помилка'}`
          : `Failed to create package: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  const handleEditPackage = async () => {
    if (!selectedPackage) return;

    try {
      await updatePackage(selectedPackage.id, {
        name: editFormData.name,
        description: editFormData.description,
        price: editFormData.price,
        currency: editFormData.currency,
        max_businesses: editFormData.max_businesses,
        target_audience: editFormData.target_audience,
        is_active: editFormData.is_active,
        is_popular: editFormData.is_popular
      });

      setEditDialogOpen(false);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Пакет оновлено успішно' : 'Package updated successfully',
        severity: 'success'
      });
      fetchPackages();
    } catch (err) {
      console.error('Failed to update package:', err);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка оновлення пакета' : 'Failed to update package',
        severity: 'error'
      });
    }
  };





  const openEditDialog = (pkg: Package) => {
    setSelectedPackage(pkg);
    setEditFormData({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price,
      currency: pkg.currency,
      max_businesses: pkg.max_businesses,
      target_audience: pkg.target_audience,
      is_active: pkg.is_active || false,
      is_popular: (pkg as any).is_popular || false
    });
    setEditDialogOpen(true);
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat(language === 'ua' ? 'uk-UA' : 'en-US', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  const getBusinessLimitText = (limit: number) => {
    return limit.toString();
  };

  const handleSyncBusinessStatuses = async () => {
    try {
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Починаємо синхронізацію...' : 'Starting synchronization...',
        severity: 'info'
      });

      const result = await syncBusinessStatuses();

      setSnackbar({
        open: true,
        message: language === 'ua'
          ? `Синхронізація завершена: ${result.message}`
          : `Synchronization completed: ${result.message}`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to sync business statuses:', err);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка синхронізації статусів бізнесів' : 'Failed to sync business statuses',
        severity: 'error'
      });
    }
  };

  // Menu handlers
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedPackageForMenu(null);
  };

  const handleCopyPackage = async () => {
    if (!selectedPackageForMenu) return;

    try {
      // Create a copy of the package with a new name
      const packageData = {
        name: `${selectedPackageForMenu.name} (Копія)`,
        description: selectedPackageForMenu.description,
        price: selectedPackageForMenu.price,
        currency: selectedPackageForMenu.currency,
        max_businesses: selectedPackageForMenu.max_businesses,
        target_audience: selectedPackageForMenu.target_audience,
        is_active: selectedPackageForMenu.is_active,
        is_popular: (selectedPackageForMenu as any).is_popular
      };

      // Create the package using the API
      await createPackage(packageData);

      // Refresh the packages list
      await fetchPackages();

      handleMenuClose();

      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Пакет успішно скопійовано' : 'Package successfully copied',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to copy package:', error);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка копіювання пакета' : 'Failed to copy package',
        severity: 'error'
      });
    }
  };

  const handleDeletePackageFromMenu = async () => {
    if (!selectedPackageForMenu) return;

    // Check if package is assigned to users
    // For now, we'll just show a confirmation - in a real implementation,
    // you'd check the database first
    const confirmMessage = language === 'ua'
      ? `Ви впевнені, що хочете видалити пакет "${selectedPackageForMenu.name}"? Це може вплинути на користувачів, які його використовують.`
      : `Are you sure you want to delete package "${selectedPackageForMenu.name}"? This may affect users who are using it.`;

    if (!confirm(confirmMessage)) {
      handleMenuClose();
      return;
    }

    try {
      // TODO: Implement actual delete API call
      console.log('Deleting package from menu:', selectedPackageForMenu.id);

      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Пакет видалено успішно' : 'Package deleted successfully',
        severity: 'success'
      });
      fetchPackages();
    } catch (err) {
      console.error('Failed to delete package:', err);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка видалення пакета' : 'Failed to delete package',
        severity: 'error'
      });
    }

    handleMenuClose();
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">Завантаження...</Typography>
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Layout>
    );
  }

  // Use all packages without filtering
  const filteredPackages = packages;

  return (
    <div className="business-settings">
      <div className="settings-header">
        <h4>
          <BusinessIcon sx={{ color: 'primary.main', mr: 1 }} />
          {language === 'ua' ? 'Управління пакетами' : 'Package Management'}
        </h4>
        {/* <p>{language === 'ua' ? 'Створюйте та керуйте тарифними планами' : 'Create and manage subscription plans'}</p> */}
      </div>

      <div className="settings-content">

        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              minWidth: { xs: 120, sm: 'auto' }
            }
          }}
        >
          <Tab
            icon={<BusinessIcon />}
            iconPosition="start"
            label={language === 'ua' ? 'Пакети' : 'Packages'}
          />
          <Tab
            icon={<SecurityIcon />}
            iconPosition="start"
            label={language === 'ua' ? 'Дозволи ролей' : 'Role Permissions'}
          />
          <Tab
            icon={<TimelineIcon />}
            iconPosition="start"
            label={language === 'ua' ? 'Аналітика' : 'Analytics'}
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label={language === 'ua' ? 'Історія підписок' : 'Subscription History'}
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label={language === 'ua' ? 'Логи' : 'Logs'}
          />
        </Tabs>

        <Box sx={{ mt: 3 }}>
          {/* Packages Tab Content */}
          {tabValue === 0 && (
            <div className="packages-content" style={{ padding: '24px' }}>


              {/* Add Package Button */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{
                    '& .MuiButton-startIcon': {
                      marginRight: { xs: 0, sm: '8px' }
                    }
                  }}
                >
                  <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {language === 'ua' ? 'Новий пакет' : 'New Package'}
                  </Box>
                </Button>
              </Box>



              {/* Packages Grid */}
              <Grid container spacing={3}>
                {/* Client Packages Section */}
                {filteredPackages.some(pkg => pkg.target_audience === 'clients') && (
                  <>
                    <Grid item xs={12}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                        mt: 1
                      }}>
                        <Typography variant="h6" sx={{
                          color: 'success.main',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          <PeopleIcon /> {language === 'ua' ? 'Пакети для клієнтів' : 'Client Packages'}
                        </Typography>
                        <Box sx={{
                          height: '2px',
                          flex: 1,
                          background: 'linear-gradient(90deg, #4caf50, transparent)',
                          borderRadius: 1
                        }} />
                      </Box>
                    </Grid>
                    {filteredPackages.filter(pkg => pkg.target_audience === 'clients').map((pkg) => (
                      <Grid item xs={12} sm={6} lg={4} key={pkg.id}>
                        <PackageCard
                          pkg={{
                            ...pkg,
                            color: '#4caf50', // Green for clients
                            description: pkg.description || '',
                            target_audience: pkg.target_audience,
                            max_businesses: pkg.max_businesses,
                            is_active: pkg.is_active
                          }}
                          billingPeriod={billingPeriod}
                          displayCurrency={displayCurrency}
                          onBillingPeriodChange={setBillingPeriod}
                          onDisplayCurrencyChange={setDisplayCurrency}
                          onSelect={() => openEditDialog(pkg)}
                          mode="admin"
                        />
                      </Grid>
                    ))}
                  </>
                )}

                {/* Master Packages Section */}
                {filteredPackages.some(pkg => pkg.target_audience === 'masters') && (
                  <>
                    <Grid item xs={12}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                        mt: 1
                      }}>
                        <Typography variant="h6" sx={{
                          color: 'primary.main',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          <BuildIcon /> {language === 'ua' ? 'Пакети для майстрів' : 'Master Packages'}
                        </Typography>
                        <Box sx={{
                          height: '2px',
                          flex: 1,
                          background: 'linear-gradient(90deg, #1976d2, transparent)',
                          borderRadius: 1
                        }} />
                      </Box>
                    </Grid>
                    {filteredPackages.filter(pkg => pkg.target_audience === 'masters').map((pkg) => (
                      <Grid item xs={12} sm={6} lg={4} key={pkg.id}>
                        <PackageCard
                          pkg={{
                            ...pkg,
                            color: '#1976d2', // Blue for masters
                            description: pkg.description || '',
                            target_audience: pkg.target_audience,
                            max_businesses: pkg.max_businesses,
                            is_active: pkg.is_active
                          }}
                          billingPeriod={billingPeriod}
                          displayCurrency={displayCurrency}
                          onBillingPeriodChange={setBillingPeriod}
                          onDisplayCurrencyChange={setDisplayCurrency}
                          onSelect={() => openEditDialog(pkg)}
                          mode="admin"
                        />
                      </Grid>
                    ))}
                  </>
                )}

                {/* Owner Packages Section */}
                {filteredPackages.some(pkg => pkg.target_audience === 'owners') && (
                  <>
                    <Grid item xs={12}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                        mt: 1
                      }}>
                        <Typography variant="h6" sx={{
                          color: 'secondary.main',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          <StarIcon /> {language === 'ua' ? 'Пакети для власників' : 'Owner Packages'}
                        </Typography>
                        <Box sx={{
                          height: '2px',
                          flex: 1,
                          background: 'linear-gradient(90deg, #9c27b0, transparent)',
                          borderRadius: 1
                        }} />
                      </Box>
                    </Grid>
                    {filteredPackages.filter(pkg => pkg.target_audience === 'owners').map((pkg) => (
                      <Grid item xs={12} sm={6} lg={4} key={pkg.id}>
                        <PackageCard
                          pkg={{
                            ...pkg,
                            color: '#9c27b0', // Purple for owners
                            description: pkg.description || '',
                            target_audience: pkg.target_audience,
                            max_businesses: pkg.max_businesses,
                            is_active: pkg.is_active
                          }}
                          billingPeriod={billingPeriod}
                          displayCurrency={displayCurrency}
                          onBillingPeriodChange={setBillingPeriod}
                          onDisplayCurrencyChange={setDisplayCurrency}
                          onSelect={() => openEditDialog(pkg)}
                          mode="admin"
                        />
                      </Grid>
                    ))}
                  </>
                )}

                {filteredPackages.length === 0 && (
                  <Grid item xs={12}>
                    <div className="no-packages">
                      <BusinessIcon />
                      <h3>{language === 'ua' ? 'Пакетів не знайдено' : 'No packages found'}</h3>
                      <p>
                        {language === 'ua' ? 'Створіть перший пакет або змініть фільтри пошуку' : 'Create your first package or adjust search filters'}
                      </p>
                    </div>
                  </Grid>
                )}
              </Grid>
            </div>
          )}
          {tabValue === 1 && (
            <div className="permissions-section">
              <div className="section-header">
                <SecurityIcon />
                <h2 className="section-title">
                  {language === 'ua' ? 'Управління дозволами ролей' : 'Role Permissions Management'}
                </h2>
              </div>
              <p className="section-description">
                {language === 'ua'
                  ? 'Налаштуйте дозволи для різних ролей користувачів системи'
                  : 'Configure permissions for different user roles in the system'
                }
              </p>

              <div className="permissions-matrix" style={{ overflowX: 'auto' }}>
                <table className="permissions-table">
                  <thead>
                    <tr>
                      <th>{language === 'ua' ? 'Роль / Дозвіл' : 'Role / Permission'}</th>
                      <th>{language === 'ua' ? 'Створити бізнес' : 'Create Business'}</th>
                      <th>{language === 'ua' ? 'Редагувати бізнес' : 'Edit Business'}</th>
                      <th>{language === 'ua' ? 'Видалити бізнес' : 'Delete Business'}</th>
                      <th>{language === 'ua' ? 'Керувати послугами' : 'Manage Services'}</th>
                      <th>{language === 'ua' ? 'Керувати співробітниками' : 'Manage Employees'}</th>
                      <th>{language === 'ua' ? 'Перегляд аналітики' : 'View Analytics'}</th>
                      <th>{language === 'ua' ? 'Керувати клієнтами' : 'Manage Clients'}</th>
                      <th>{language === 'ua' ? 'Перегляд записів' : 'View Appointments'}</th>
                      <th>{language === 'ua' ? 'Керувати записами' : 'Manage Appointments'}</th>
                      <th>{language === 'ua' ? 'Доступ до підтримки' : 'Access Support'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissionsLoading ? (
                      <tr>
                        <td colSpan={11} className="text-center">
                          {language === 'ua' ? 'Завантаження дозволів...' : 'Loading permissions...'}
                        </td>
                      </tr>
                    ) : (
                      ['client', 'master', 'owner', 'admin', 'super_admin'].map(role => (
                        <tr key={role}>
                          <td className="role-cell">
                            {role === 'client' && (language === 'ua' ? 'Клієнт (Client)' : 'Client')}
                            {role === 'master' && (language === 'ua' ? 'Майстер (Master)' : 'Master')}
                            {role === 'owner' && (language === 'ua' ? 'Власник (Owner)' : 'Owner')}
                            {role === 'admin' && (language === 'ua' ? 'Адміністратор (Admin)' : 'Admin')}
                            {role === 'super_admin' && (language === 'ua' ? 'Супер Адміністратор' : 'Super Admin')}
                          </td>
                          {permissions.map(permission => (
                            <td key={permission.name}>
                              <Checkbox
                                checked={(rolePermissions[role] || []).includes(permission.name)}
                                onChange={(e) => handlePermissionChange(role, permission.name, e.target.checked)}
                                disabled={role === 'client' || role === 'admin'}
                                size="small"
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="permissions-actions">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSavePermissions}
                  disabled={permissionsLoading}
                  sx={{
                    mr: 2,
                    '& .MuiButton-startIcon': {
                      marginRight: { xs: 0, sm: '8px' }
                    }
                  }}
                >
                  <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {permissionsLoading ? (language === 'ua' ? 'Збереження...' : 'Saving...') : (language === 'ua' ? 'Зберегти зміни' : 'Save Changes')}
                  </Box>
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleResetPermissions}
                  disabled={permissionsLoading}
                  sx={{
                    '& .MuiButton-startIcon': {
                      marginRight: { xs: 0, sm: '8px' }
                    }
                  }}
                >
                  <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {language === 'ua' ? 'Скинути до початкового' : 'Reset to Default'}
                  </Box>
                </Button>
              </div>
            </div>
          )}

          {tabValue === 2 && (
            /* Analytics Tab */
            <Grid container spacing={3}>
              {/* Revenue Chart */}
              <Grid item xs={12} lg={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MonetizationOnIcon />
                      {language === 'ua' ? 'Дохід по пакетах' : 'Revenue by Package'}
                    </Typography>
                    <Box sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      borderRadius: 1
                    }}>
                      <Typography color="text.secondary">
                        {language === 'ua' ? 'Графік доходів буде додано' : 'Revenue chart coming soon'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Key Metrics */}
              <Grid item xs={12} lg={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon />
                      {language === 'ua' ? 'Ключові метрики' : 'Key Metrics'}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                          <Typography variant="h4" color="primary.contrastText" fontWeight={600}>
                            {packages.length}
                          </Typography>
                          <Typography variant="caption" color="primary.contrastText">
                            {language === 'ua' ? 'Пакетів' : 'Packages'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h4" color="success.contrastText" fontWeight={600}>
                            {packages.filter(p => p.is_active).length}
                          </Typography>
                          <Typography variant="caption" color="success.contrastText">
                            {language === 'ua' ? 'Активних' : 'Active'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                          <Typography variant="h4" color="warning.contrastText" fontWeight={600}>
                            {packages.reduce((sum, pkg) => sum + pkg.price, 0).toFixed(0)}
                          </Typography>
                          <Typography variant="caption" color="warning.contrastText">
                            {language === 'ua' ? 'Загальна вартість' : 'Total Value'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                          <Typography variant="h4" color="info.contrastText" fontWeight={600}>
                            {packages.reduce((sum, pkg) => sum + pkg.max_businesses, 0)}
                          </Typography>
                          <Typography variant="caption" color="info.contrastText">
                            {language === 'ua' ? 'Бізнесів' : 'Businesses'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Usage Stats */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon />
                      {language === 'ua' ? 'Статистика використання' : 'Usage Statistics'}
                    </Typography>
                    <Box sx={{
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      borderRadius: 1
                    }}>
                      <Typography color="text.secondary">
                        {language === 'ua' ? 'Статистика використання буде додана' : 'Usage statistics coming soon'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tabValue === 3 && (
            /* Subscription History Tab */
            <Grid container spacing={3}>
              {/* Subscription History Header */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon />
                      {language === 'ua' ? 'Історія підписок' : 'Subscription History'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {language === 'ua'
                        ? 'Перегляньте історію підписок користувачів на пакети'
                        : 'View the history of user subscriptions to packages'
                      }
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                          setSnackbar({
                            open: true,
                            message: language === 'ua' ? 'Оновлення історії підписок...' : 'Refreshing subscription history...',
                            severity: 'info'
                          });
                        }}
                        size="large"
                        sx={{
                          px: 3,
                          py: 1.5,
                          '& .MuiButton-startIcon': {
                            marginRight: { xs: 0, sm: '8px' }
                          }
                        }}
                      >
                        <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          {language === 'ua' ? 'Оновити' : 'Refresh'}
                        </Box>
                      </Button>

                      <Button
                        variant="outlined"
                        startIcon={<TimelineIcon />}
                        onClick={() => {
                          setSnackbar({
                            open: true,
                            message: language === 'ua' ? 'Експорт буде додано' : 'Export functionality coming soon',
                            severity: 'info'
                          });
                        }}
                        size="large"
                        sx={{
                          px: 3,
                          py: 1.5,
                          '& .MuiButton-startIcon': {
                            marginRight: { xs: 0, sm: '8px' }
                          }
                        }}
                      >
                        <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          {language === 'ua' ? 'Експорт' : 'Export'}
                        </Box>
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Subscription History Table */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{
                      height: 400,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '2px dashed #bdbdbd'
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <HistoryIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {language === 'ua' ? 'Історія підписок буде додана' : 'Subscription history coming soon'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {language === 'ua'
                            ? 'Тут буде відображатися історія підписок користувачів на пакети'
                            : 'Subscription history for user packages will be displayed here'
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tabValue === 4 && (
            /* Logs Tab */
            <Grid container spacing={3}>
              {/* Processing Actions */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SyncIcon />
                      {language === 'ua' ? 'Обробка даних' : 'Data Processing'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {language === 'ua'
                        ? 'Інструменти для синхронізації та обробки даних пакетів та бізнесів'
                        : 'Tools for synchronizing and processing package and business data'
                      }
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        startIcon={<SyncIcon />}
                        onClick={handleSyncBusinessStatuses}
                        size="large"
                        sx={{
                          px: 3,
                          py: 1.5,
                          '& .MuiButton-startIcon': {
                            marginRight: { xs: 0, sm: '8px' }
                          }
                        }}
                      >
                        <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          {language === 'ua' ? 'Синхронізувати статуси бізнесів' : 'Sync Business Statuses'}
                        </Box>
                      </Button>

                      <Button
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={() => {
                          setSnackbar({
                            open: true,
                            message: language === 'ua' ? 'Історія операцій буде додана' : 'Operation history coming soon',
                            severity: 'info'
                          });
                        }}
                        size="large"
                        sx={{
                          px: 3,
                          py: 1.5,
                          '& .MuiButton-startIcon': {
                            marginRight: { xs: 0, sm: '8px' }
                          }
                        }}
                      >
                        <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          {language === 'ua' ? 'Переглянути історію' : 'View History'}
                        </Box>
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Processing Results */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon />
                      {language === 'ua' ? 'Результати обробки' : 'Processing Results'}
                    </Typography>
                    <Box sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '2px dashed #bdbdbd'
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <HistoryIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {language === 'ua' ? 'Немає результатів обробки' : 'No processing results'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {language === 'ua'
                            ? 'Використовуйте кнопки вище для запуску операцій обробки'
                            : 'Use the buttons above to start processing operations'
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}



        {/* Create Package Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {language === 'ua' ? 'Створити новий пакет' : 'Create New Package'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={language === 'ua' ? 'Назва пакета' : 'Package Name'}
              value={createFormData.name}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={language === 'ua' ? 'Опис' : 'Description'}
              value={createFormData.description}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, description: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label={language === 'ua' ? 'Ціна' : 'Price'}
                type="number"
                value={createFormData.price}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>{language === 'ua' ? 'Валюта' : 'Currency'}</InputLabel>
                <Select
                  value={createFormData.currency}
                  label={language === 'ua' ? 'Валюта' : 'Currency'}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, currency: e.target.value }))}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="UAH">UAH</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label={language === 'ua' ? 'Максимальна кількість бізнесів' : 'Max Businesses'}
              type="number"
              value={createFormData.max_businesses}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, max_businesses: parseInt(e.target.value) || 0 }))}
              margin="normal"
              helperText={language === 'ua' ? '0 = необмежено' : '0 = unlimited'}
              InputProps={{ inputProps: { min: 0 } }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>{language === 'ua' ? 'Цільова аудиторія' : 'Target Audience'}</InputLabel>
              <Select
                value={createFormData.target_audience}
                label={language === 'ua' ? 'Цільова аудиторія' : 'Target Audience'}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, target_audience: e.target.value }))}
              >
                <MenuItem value="clients">{language === 'ua' ? 'Клієнти' : 'Clients'}</MenuItem>
                <MenuItem value="masters">{language === 'ua' ? 'Майстри' : 'Masters'}</MenuItem>
                <MenuItem value="owners">{language === 'ua' ? 'Власники' : 'Owners'}</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createFormData.is_active}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label={language === 'ua' ? 'Активний' : 'Active'}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createFormData.is_popular}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, is_popular: e.target.checked }))}
                  />
                }
                label={language === 'ua' ? 'Популярний' : 'Popular'}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>
              {language === 'ua' ? 'Скасувати' : 'Cancel'}
            </Button>
            <Button onClick={handleCreatePackage} variant="contained">
              {language === 'ua' ? 'Створити' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Package Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {language === 'ua' ? 'Редагувати пакет' : 'Edit Package'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={language === 'ua' ? 'Назва пакета' : 'Package Name'}
              value={editFormData.name}
              onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={language === 'ua' ? 'Опис' : 'Description'}
              value={editFormData.description}
              onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label={language === 'ua' ? 'Ціна' : 'Price'}
                type="number"
                value={editFormData.price}
                onChange={(e) => setEditFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>{language === 'ua' ? 'Валюта' : 'Currency'}</InputLabel>
                <Select
                  value={editFormData.currency}
                  label={language === 'ua' ? 'Валюта' : 'Currency'}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, currency: e.target.value }))}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="UAH">UAH</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label={language === 'ua' ? 'Максимальна кількість бізнесів' : 'Max Businesses'}
              type="number"
              value={editFormData.max_businesses}
              onChange={(e) => setEditFormData(prev => ({ ...prev, max_businesses: parseInt(e.target.value) || 0 }))}
              margin="normal"
              InputProps={{ inputProps: { min: 0 } }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>{language === 'ua' ? 'Цільова аудиторія' : 'Target Audience'}</InputLabel>
              <Select
                value={editFormData.target_audience}
                label={language === 'ua' ? 'Цільова аудиторія' : 'Target Audience'}
                onChange={(e) => setEditFormData(prev => ({ ...prev, target_audience: e.target.value }))}
              >
                <MenuItem value="clients">{language === 'ua' ? 'Клієнти' : 'Clients'}</MenuItem>
                <MenuItem value="masters">{language === 'ua' ? 'Майстри' : 'Masters'}</MenuItem>
                <MenuItem value="owners">{language === 'ua' ? 'Власники' : 'Owners'}</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.is_active}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label={language === 'ua' ? 'Активний' : 'Active'}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.is_popular}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, is_popular: e.target.checked }))}
                  />
                }
                label={language === 'ua' ? 'Популярний' : 'Popular'}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>
              {language === 'ua' ? 'Скасувати' : 'Cancel'}
            </Button>
            <Button onClick={handleEditPackage} variant="contained">
              {language === 'ua' ? 'Зберегти' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Package Dialog */}
        <Dialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {language === 'ua' ? 'Призначити пакет користувачу' : 'Assign Package to User'}
          </DialogTitle>
          <DialogContent>
            {selectedPackage && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">{selectedPackage.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatPrice(selectedPackage.price, selectedPackage.currency)} •
                  {getBusinessLimitText(selectedPackage.max_businesses)} {language === 'ua' ? 'бізнесів' : 'businesses'}
                </Typography>
              </Box>
            )}

            {/* TODO: Add user search and selection component */}
            <Typography color="text.secondary">
              {language === 'ua'
                ? 'Функціонал призначення пакетів користувачам буде додано після реалізації пошуку користувачів'
                : 'Package assignment functionality will be added after user search implementation'
              }
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignDialogOpen(false)}>
              {language === 'ua' ? 'Закрити' : 'Close'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Package Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MuiMenuItem onClick={handleCopyPackage}>
            {language === 'ua' ? 'Копіювати пакет' : 'Copy Package'}
          </MuiMenuItem>
          <MuiMenuItem
            onClick={handleDeletePackageFromMenu}
            sx={{ color: 'error.main' }}
          >
            {language === 'ua' ? 'Видалити пакет' : 'Delete Package'}
          </MuiMenuItem>
        </Menu>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        </Box>
      </div>
    </div>
  );
};

export default PackageManagement;
